import {
  Component,
  ElementRef,
  PLATFORM_ID,
  afterNextRender,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type { ECharts, EChartsOption } from 'echarts';
import { withBodyTooltips } from './echarts-tooltip.util';
import { suppressEchartsTitle } from '@features/datastories/shared/datastory-chart.util';

/**
 * Wrapper mínimo do ECharts para o front-22 (Angular 22 + SSR).
 * O ECharts é carregado dinamicamente e inicializado apenas no browser
 * (via `afterNextRender`), evitando referências a `window`/`document` no SSR.
 */
@Component({
  selector: 'app-echart',
  standalone: true,
  host: { class: 'block h-full w-full min-h-0' },
  template: `<div #host class="h-full w-full min-h-0"></div>`,
})
export class EchartComponent {
  readonly options = input<EChartsOption | null>(null);
  readonly hideTitle = input(false);

  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private chart: ECharts | null = null;
  private resizeObserver?: ResizeObserver;
  private initObserver?: ResizeObserver;
  private echartsModule: typeof import('echarts') | null = null;

  constructor() {
    if (!this.isBrowser) return;

    afterNextRender(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => void this.ensureChart());
      });
    });

    effect(() => {
      this.options();
      this.hideTitle();
      this.applyOptions();
    });
  }

  private async ensureChart(): Promise<void> {
    const el = this.host().nativeElement;
    if (this.chart) {
      this.applyOptions();
      return;
    }

    const tryInit = async (): Promise<boolean> => {
      if (this.chart) return true;
      if (!this.hasSize(el)) return false;

      this.echartsModule ??= await import('echarts');
      if (this.chart) return true;
      if (!this.hasSize(el)) return false;

      this.chart = this.echartsModule.init(el, undefined, { renderer: 'canvas' });
      this.initObserver?.disconnect();
      this.initObserver = undefined;
      this.applyOptions();
      this.attachResizeObserver(el);
      return true;
    };

    if (await tryInit()) return;

    this.initObserver = new ResizeObserver(() => {
      void tryInit();
    });
    const container = el.parentElement ?? el;
    this.initObserver.observe(container);
    this.initObserver.observe(el);
  }

  private attachResizeObserver(el: HTMLElement): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      if (this.hasSize(el)) this.scheduleResize();
    });
    const container = el.parentElement ?? el;
    this.resizeObserver.observe(container);
    this.scheduleResize();
  }

  private hasSize(el: HTMLElement): boolean {
    return el.clientWidth > 0 && el.clientHeight > 0;
  }

  private applyOptions(): void {
    const opts = this.options();
    if (this.chart && opts) {
      const prepared = this.hideTitle() ? suppressEchartsTitle(opts) : opts;
      this.chart.setOption(withBodyTooltips(prepared), {
        notMerge: true,
        replaceMerge: ['title'],
      });
      this.scheduleResize();
    }
  }

  /** Instância viva — `null` no SSR e antes do primeiro render com dimensão. */
  getChart(): ECharts | null {
    return this.chart;
  }

  /** Dimensão atual do canvas, usada para renderizar o SVG de export no mesmo tamanho. */
  getSize(): { width: number; height: number } {
    const el = this.host().nativeElement;
    return {
      width: Math.max(el.clientWidth, 640),
      height: Math.max(el.clientHeight, 360),
    };
  }

  private scheduleResize(): void {
    if (!this.chart) return;
    const el = this.host().nativeElement;
    if (!this.hasSize(el)) return;
    requestAnimationFrame(() => {
      this.chart?.resize();
    });
  }

  ngOnDestroy(): void {
    this.initObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
  }
}
